import type { Lobby, LobbyId, LobbyLifecycleState, Pseudonym, SessionId } from '@/domain/game';

// Application-layer port for the lobby REST surface. The concrete adapter
// (`HttpLobbyClient` in `infrastructure/`) speaks the OpenAPI contract
// declared in `game/api/openapi.yaml`. Routes receive an instance through
// the TanStack Router context so `ui/` keeps zero `infrastructure/` imports
// per ADR-0002 §7. One method per `operationId`.

export interface LobbyClient {
  /**
   * `POST /v1/lobbies`. Mints a new lobby in WAITING with the calling
   * player as the sole member and owner. Throws {@link LobbyClientError}
   * on a non-2xx response — callers `switch` on `error.kind` to render
   * the matching UI banner.
   *
   * Resolves with the freshly-minted lobby intersected with its
   * server-issued `LobbyId`. Unlike {@link getLobby}, the `id` is not
   * known from the URL on this seam — the home-route "Créer une partie
   * multijoueur" button reads it to navigate to `/lobby/:lobbyId`.
   */
  createLobby(args: {
    ownerSessionId: SessionId;
    ownerPseudonym: Pseudonym;
  }): Promise<Lobby & { readonly id: LobbyId }>;

  /**
   * `POST /v1/lobbies/{lobbyId}/ownership`. Claims an ownerless
   * (`owner_user_id IS NULL`) non-terminal lobby for the calling
   * signed-in player (ADR-0098 §2), gated by the caller's active-game
   * quota. Cookie-authed — no request body. Resolves with the `Lobby`
   * snapshot carrying the caller as owner. Throws {@link LobbyClientError}
   * with `kind: 'validation'` (403 forbidden — not present / already
   * owned), `kind: 'unauthorized'` (401), or `kind: 'not-found'` (404).
   */
  claimOwnership(lobbyId: LobbyId): Promise<Lobby>;

  /**
   * `DELETE /v1/lobbies/{lobbyId}/ownership`. Relinquishes the calling
   * owner's lease so the lobby becomes **ownerless** (ADR-0098 §2), leaving
   * the caller's active-game quota synchronously. Cookie-authed — no request
   * body. Resolves with the now-ownerless `Lobby` snapshot. This synchronous
   * REST write replaces the racy WS `leaveLobby`-then-create dance the
   * "Démarrer une nouvelle partie" flow used to run. Throws
   * {@link LobbyClientError} with `kind: 'validation'` (403 — not the owner),
   * `kind: 'unauthorized'` (401), or `kind: 'not-found'` (404).
   */
  relinquishOwnership(lobbyId: LobbyId): Promise<Lobby>;

  /**
   * `DELETE /v1/lobbies/{lobbyId}/membership`. Drops the caller's seat from
   * a lobby they are not currently on the WebSocket for — the "quitter /
   * supprimer" affordance on the multiplayer game lists (ADR-0098 §6,
   * 2026-07-08 amendment). When the caller is the owner, ownership is also
   * relinquished; composed with the ADR-0055 destroy-ownerless-and-empty
   * rule this yields delete-if-alone / leave-if-others. Cookie-authed — no
   * request body. Resolves on `204`. Throws {@link LobbyClientError} with
   * `kind: 'unauthorized'` (401 — missing/invalid session), `kind:
   * 'validation'` (403 — no seat to leave), or `kind: 'not-found'` (404).
   */
  leaveLobby(lobbyId: LobbyId): Promise<void>;

  /**
   * `GET /v1/lobbies/{lobbyId}`. Bootstraps the lobby route loader before
   * the WebSocket opens. Throws {@link LobbyClientError} with
   * `kind: 'not-found'` when the lobby has never existed or has been GC'd
   * (in-memory v1 — ADR-0018 §3).
   */
  getLobby(lobbyId: LobbyId): Promise<Lobby>;

  /**
   * `GET /v1/lobbies/by-code/{code}`. Resolves a human-friendly join
   * code to the canonical lobby resource. Powers the Accueil "Rejoindre
   * avec un code" flow — the resolved `id` is the same `LobbyId` the
   * URL-share path uses, so the caller can navigate to `/lobby/:lobbyId`
   * and let the existing route loader open the WebSocket.
   *
   * Throws {@link LobbyClientError} with:
   *   - `kind: 'validation'` for a malformed code (400 — pattern reject),
   *   - `kind: 'not-found'` when no lobby carries the supplied code (404).
   */
  findByCode(code: string): Promise<Lobby & { readonly id: LobbyId }>;

  /**
   * `GET /v1/sessions/{sessionId}/lobbies`. Lists the lobbies the
   * calling session is a member of, in every lifecycle state, ordered
   * by `lastActivityAt` descending. Powers the Accueil "Mes parties"
   * surface (ADR-0039). An empty array is the "no lobbies" answer —
   * there is no 404 here, which would leak whether the session has ever
   * played. Not paginated.
   */
  listMyLobbies(sessionId: SessionId): Promise<readonly LobbySummary[]>;

  /**
   * `GET /v1/users/me/lobbies`. Cookie-authed cross-device variant
   * (ADR-0066): union of lobbies whose seats carry the caller's userId,
   * same shape/filter/ordering as `listMyLobbies`. The loader picks this
   * when `auth.status === 'authed'`.
   */
  listMyLobbiesForUser(): Promise<readonly LobbySummary[]>;

  /**
   * `POST /v1/lobbies/players/rebind`. Cookie-authed (`__Secure-ws_session`).
   * Called on the anon→authed transition so existing lobby seats keyed by
   * the local session id are linked to the now-signed-in user. Idempotent;
   * resolves on 204. Rejects on 401 (no/invalid cookie) or 5xx.
   */
  rebindLobbySessions(anonSessionId: SessionId): Promise<void>;

  /**
   * `POST /v1/lobbies/players/unbind`. Cookie-authed. Called before
   * `authClient.logout()` so the seat reverts to the anon pseudonym
   * the user will see again after sign-out. Idempotent.
   */
  unbindLobbySessions(anonPseudonym: string): Promise<void>;
}

// Lightweight projection of a lobby for the "Mes parties" list. The
// server-side adapter computes `playerCount` so the summary endpoint
// avoids loading the full player list (and to keep this seam thin).
// `connectedCount` is the live-WS subset of `playerCount` — drives the X / Y display.
// `title` is absent when the owner did not set one at creation; per
// ADR-0003 §6 optional means absent on the wire, never `null`. `progress`
// drives the per-row progress bar.
export interface LobbySummary {
  readonly id: LobbyId;
  readonly code: string;
  readonly state: LobbyLifecycleState;
  readonly gridConfig: { readonly width: number; readonly height: number };
  readonly playerCount: number;
  readonly connectedCount: number;
  readonly lastActivityAt: string;
  readonly progress: LobbyProgress;
  readonly title?: string;
  // ADR-0098: true when the lobby is ownerless — the "Reprendre" row claims it rather than plain-navigating. Absent ⇒ owned.
  readonly ownerless?: boolean;
}

export interface LobbyProgress {
  readonly solvedCells: number;
  readonly totalCells: number;
}

// Typed error envelope. One concrete `Error` subclass with a `kind`
// discriminator so callers route on `kind` instead of HTTP status codes.
// The wire-level problem-details body is preserved on `problem` for
// diagnostic UIs.

export type LobbyClientErrorKind =
  | 'validation' // 400 — request body malformed.
  | 'unauthorized' // 401 — hosting requires a signed-in player (ADR-0083).
  | 'not-found' // 404 — lobby id unknown / GC'd.
  | 'transient' // 5xx — server-side, safe to retry.
  | 'upstream-unavailable'; // network error — DNS / connection refused / abort.

export interface ProblemDetails {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
}

export class LobbyClientError extends Error {
  readonly kind: LobbyClientErrorKind;
  readonly status: number | null;
  readonly problem: ProblemDetails | null;

  constructor(args: {
    kind: LobbyClientErrorKind;
    status: number | null;
    problem: ProblemDetails | null;
    message: string;
  }) {
    super(args.message);
    this.name = 'LobbyClientError';
    this.kind = args.kind;
    this.status = args.status;
    this.problem = args.problem;
  }
}
